const audioCtxRef = { current: null };

// Call once on any user gesture (click/keydown) to pre-unlock AudioContext.
const unlock = () => {
  try {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  } catch (e) { /* ignore */ }
};

// Ask for browser notification permission on the first user gesture so we can
// alert the restaurant even when the tab is in the background / not focused.
const requestNotificationPermission = () => {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  } catch (e) { /* ignore */ }
};

const onGesture = () => {
  unlock();
  requestNotificationPermission();
};

if (typeof window !== 'undefined') {
  window.addEventListener('click', onGesture, { once: false, passive: true });
  window.addEventListener('keydown', onGesture, { once: false, passive: true });
  window.addEventListener('touchstart', onGesture, { once: false, passive: true });
}

const getAudioContext = () => {
  if (!audioCtxRef.current) {
    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtxRef.current;
};

const playBeep = (frequency = 800, duration = 200, volume = 0.3) => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => {
        // retry after resume
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = frequency;
        osc.type = 'sine';
        gain.gain.setValueAtTime(volume, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + duration / 1000);
      }).catch(() => {});
      return;
    }
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);
  } catch (e) {
    
  }
};

const playOrderNotification = () => {
  playBeep(880, 150, 0.4);
  setTimeout(() => playBeep(880, 150, 0.4), 200);
  setTimeout(() => playBeep(1100, 300, 0.4), 450);
};

const notifiedOrdersRef = new Set();

const hasNotified = (orderId) => {
  return notifiedOrdersRef.has(orderId);
};

const markNotified = (orderId) => {
  if (orderId) {
    notifiedOrdersRef.add(orderId);
    if (notifiedOrdersRef.size > 100) {
      const arr = Array.from(notifiedOrdersRef);
      notifiedOrdersRef.clear();
      arr.slice(-50).forEach(id => notifiedOrdersRef.add(id));
    }
  }
};

const clearNotified = () => {
  notifiedOrdersRef.clear();
};

// ----- Persistent alarm: loops sound + notification + title flash until the
// order is accepted/rejected. Works while the tab is backgrounded. -----
const activeAlarms = new Map(); // orderId -> Notification | null
let alarmSoundTimer = null;
let titleFlashTimer = null;
let originalTitle = null;

const startTitleFlash = () => {
  if (titleFlashTimer || typeof document === 'undefined') return;
  originalTitle = document.title;
  let on = false;
  titleFlashTimer = setInterval(() => {
    on = !on;
    document.title = on ? '🔔 New Order!' : (originalTitle || 'Roll2Bowl');
  }, 1000);
};

const stopTitleFlash = () => {
  if (titleFlashTimer) {
    clearInterval(titleFlashTimer);
    titleFlashTimer = null;
  }
  if (originalTitle !== null && typeof document !== 'undefined') {
    document.title = originalTitle;
    originalTitle = null;
  }
};

const showBrowserNotification = (order) => {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
    const orderLabel = order?.orderId || order?._id || '';
    const customer = order?.customerName || order?.customer?.name || 'Customer';
    const notification = new Notification('New Order Received', {
      body: `${customer}${orderLabel ? ` · #${orderLabel}` : ''} — tap to review`,
      tag: order?._id || order?.orderId || 'new-order',
      requireInteraction: true,
    });
    notification.onclick = () => {
      try { window.focus(); } catch (e) { /* ignore */ }
      notification.close();
    };
    return notification;
  } catch (e) {
    return null;
  }
};

const startOrderAlarm = (orderId, order) => {
  if (!orderId || activeAlarms.has(orderId)) return;
  activeAlarms.set(orderId, showBrowserNotification(order));

  playOrderNotification();
  if (!alarmSoundTimer) {
    alarmSoundTimer = setInterval(() => playOrderNotification(), 3000);
  }
  startTitleFlash();
};

const stopOrderAlarm = (orderId) => {
  if (!orderId || !activeAlarms.has(orderId)) return;
  const notification = activeAlarms.get(orderId);
  try { notification?.close(); } catch (e) { /* ignore */ }
  activeAlarms.delete(orderId);

  if (activeAlarms.size === 0) {
    if (alarmSoundTimer) {
      clearInterval(alarmSoundTimer);
      alarmSoundTimer = null;
    }
    stopTitleFlash();
  }
};

export const SoundService = {
  playOrderNotification,
  playBeep,
  hasNotified,
  markNotified,
  clearNotified,
  startOrderAlarm,
  stopOrderAlarm,
};

export default SoundService;
